<template>
  <div>
    <section id="automazioni-top" class="relative overflow-hidden min-h-screen flex items-center pt-16" data-animate="hero" :class="{ 'is-visible': visible.hero }">
      <div class="absolute inset-0 bg-white dark:bg-dark transition-colors duration-300">
        <img v-if="isDark" src="/ciobai_hero_background.webp" alt="" class="absolute inset-0 w-full h-full object-cover" />
        <img v-else src="/ciobai_hero_background_light.webp" alt="" class="absolute inset-0 w-full h-full object-cover opacity-40" />
      </div>

      <div class="container mx-auto px-4 relative z-10">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p class="text-[13px] uppercase tracking-[0.2em] text-blue-500 font-medium mb-6 anim-el d1">
              {{ tr('Automazioni AI', 'AI Automations') }}
            </p>
            <h1 class="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-5 anim-el d2">
              <span class="text-gray-900 dark:text-white transition-colors duration-300">{{ tr('Automazioni intelligenti', 'Smart automations') }}</span>
              <br />
              <span class="hero-gradient-text">{{ tr('per scalare il tuo business', 'to scale your business') }}</span>
            </h1>
            <p class="text-lg md:text-xl text-gray-500 dark:text-gray-400 max-w-xl leading-relaxed mb-10 transition-colors duration-300 anim-el d3">
              {{ tr('Chatbot, agenti vocali, workflow automation e marketing AI in un unico ecosistema operativo.', 'Chatbots, voice agents, workflow automation, and AI marketing in one operating ecosystem.') }}
            </p>
            <div class="flex flex-col sm:flex-row gap-4 items-start mb-10 anim-el d4">
              <router-link to="/contact" class="group inline-flex items-center h-14 px-8 bg-gray-900 dark:bg-white text-white dark:text-dark rounded-xl font-bold text-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5">
                <Sparkles class="mr-3 h-5 w-5 transition-transform duration-300 group-hover:scale-110" />
                {{ tr('Progetta la tua automazione', 'Design your automation') }}
              </router-link>
              <router-link to="/chatbot" class="group inline-flex items-center h-14 px-8 border border-gray-200 dark:border-white/[0.08] text-gray-500 dark:text-gray-400 rounded-xl font-bold text-lg transition-all duration-300 hover:border-gray-300 dark:hover:border-white/[0.15] hover:bg-gray-50 dark:hover:bg-white/[0.03]">
                <Bot class="mr-2 h-5 w-5" />
                {{ tr('See AI Chatbots', 'See AI Chatbots') }}
              </router-link>
            </div>
            <div class="flex flex-wrap gap-6 text-sm text-gray-500 dark:text-gray-400 anim-el d5">
              <div class="flex items-center gap-2"><div class="w-6 h-6 rounded-md bg-blue-500/10 flex items-center justify-center"><Check class="h-3.5 w-3.5 text-blue-500" /></div><span>{{ tr('Riduzione costi operativi', 'Lower operational costs') }}</span></div>
              <div class="flex items-center gap-2"><div class="w-6 h-6 rounded-md bg-blue-500/10 flex items-center justify-center"><Check class="h-3.5 w-3.5 text-blue-500" /></div><span>{{ tr('Processi 24/7', '24/7 processes') }}</span></div>
              <div class="flex items-center gap-2"><div class="w-6 h-6 rounded-md bg-blue-500/10 flex items-center justify-center"><Check class="h-3.5 w-3.5 text-blue-500" /></div><span>{{ tr('Integrazione ai sistemi esistenti', 'Integration with existing systems') }}</span></div>
            </div>
          </div>

          <div ref="heroPanel" class="relative hidden lg:block anim-el d6" @mousemove="onHeroMove" @mouseleave="onHeroLeave">
            <div class="rounded-2xl border border-gray-200 dark:border-white/[0.08] overflow-hidden shadow-xl bg-white dark:bg-dark transition-colors duration-300 will-change-transform" :style="mockupTransform">
              <div class="flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border-b border-gray-200 dark:border-white/[0.06]">
                <div class="flex gap-1.5"><div class="w-2.5 h-2.5 rounded-full bg-red-400/80"></div><div class="w-2.5 h-2.5 rounded-full bg-yellow-400/80"></div><div class="w-2.5 h-2.5 rounded-full bg-green-400/80"></div></div>
                <div class="flex-1 h-6 rounded-md bg-white dark:bg-white/[0.06] mx-8 flex items-center px-3">
                  <Lock class="w-3 h-3 text-green-500 mr-1.5" />
                  <span class="text-[11px] text-gray-400 dark:text-gray-500">automation.ciobai.ch</span>
                </div>
              </div>
              <div class="p-5 space-y-4">
                <div class="grid grid-cols-2 gap-3">
                  <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-3">
                    <p class="text-[10px] text-gray-400 dark:text-gray-500 mb-1">Task automatizzati</p>
                    <p class="text-xl font-bold text-gray-900 dark:text-white tabular-nums">{{ cTasks.toLocaleString('it-IT') }}</p>
                  </div>
                  <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-3">
                    <p class="text-[10px] text-gray-400 dark:text-gray-500 mb-1">Ore risparmiate</p>
                    <p class="text-xl font-bold text-gray-900 dark:text-white tabular-nums">{{ cHours }}</p>
                  </div>
                </div>
                <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-3">
                  <p class="text-[10px] text-gray-400 dark:text-gray-500 mb-2">Pipeline operativa</p>
                  <div class="space-y-2">
                    <div v-for="(s, i) in pipelineSteps" :key="s.label" class="flex items-center gap-2">
                      <div class="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold" :class="i < activePipelineStep ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-white/[0.08] text-gray-500'">{{ i + 1 }}</div>
                      <div class="flex-1 h-2 rounded-full bg-gray-200 dark:bg-white/[0.08] overflow-hidden">
                        <div class="h-full rounded-full bg-blue-500 transition-all duration-700" :style="{ width: i < activePipelineStep ? '100%' : '0%' }"></div>
                      </div>
                      <span class="text-[10px] text-gray-500 dark:text-gray-400">{{ s.label }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white dark:from-dark to-transparent z-10"></div>
    </section>

    <section class="py-20 bg-white dark:bg-dark transition-colors duration-300" data-animate="orchestrator" :class="{ 'is-visible': visible.orchestrator }">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto text-center mb-10">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ tr('Orchestratore AI', 'AI Orchestrator') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">{{ tr('Scegli il flusso che vuoi automatizzare', 'Choose the flow you want to automate') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400">{{ tr('Una regia unica gestisce trigger, azioni e monitoraggio in tempo reale.', 'A single control layer handles triggers, actions, and real-time monitoring.') }}</p>
        </div>
        <div class="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div class="lg:col-span-1 rounded-2xl border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-4 anim-el d1">
            <button
              v-for="(scenario, idx) in scenarios"
              :key="scenario.title"
              @click="activeScenario = idx"
              class="w-full text-left rounded-xl p-3 mb-2 last:mb-0 transition-all duration-300"
              :class="activeScenario === idx ? 'bg-white dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.1]' : 'hover:bg-white/80 dark:hover:bg-white/[0.03]'"
            >
              <p class="text-sm font-semibold" :class="activeScenario === idx ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'">{{ scenario.title }}</p>
              <p class="text-xs mt-0.5" :class="activeScenario === idx ? 'text-gray-500 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'">{{ scenario.subtitle }}</p>
            </button>
          </div>
          <div class="lg:col-span-2 rounded-2xl border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.02] p-5 anim-el d2">
            <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-3">
                <p class="text-[10px] text-gray-400 dark:text-gray-500 mb-1">Trigger</p>
                <p class="text-sm font-bold text-gray-900 dark:text-white">{{ scenarios[activeScenario].trigger }}</p>
              </div>
              <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-3">
                <p class="text-[10px] text-gray-400 dark:text-gray-500 mb-1">Azione</p>
                <p class="text-sm font-bold text-gray-900 dark:text-white">{{ scenarios[activeScenario].action }}</p>
              </div>
              <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-3">
                <p class="text-[10px] text-gray-400 dark:text-gray-500 mb-1">Tempo medio</p>
                <p class="text-sm font-bold text-gray-900 dark:text-white">{{ scenarios[activeScenario].latency }}</p>
              </div>
              <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-3">
                <p class="text-[10px] text-gray-400 dark:text-gray-500 mb-1">Impatto</p>
                <p class="text-sm font-bold text-blue-600 dark:text-blue-400">{{ scenarios[activeScenario].impact }}</p>
              </div>
            </div>
            <div class="rounded-xl border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-4">
              <p class="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">{{ tr('Flow preview', 'Flow preview') }}</p>
              <div class="flex flex-wrap items-center gap-2">
                <template v-for="(step, stepIdx) in scenarios[activeScenario].flow" :key="step">
                  <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.03] px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300">{{ step }}</div>
                  <span v-if="stepIdx < scenarios[activeScenario].flow.length - 1" class="text-gray-300 dark:text-gray-600">→</span>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="chatbots" class="py-24 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="chatbots" :class="{ 'is-visible': visible.chatbots }">
      <div class="container mx-auto px-4">
        <div class="max-w-3xl mx-auto text-center mb-14">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ tr('Chatbot AI', 'AI Chatbots') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">{{ tr('Interazioni automatiche intelligenti', 'Smart automated interactions') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400">{{ tr('Gestisci supporto, vendite e informazione con bot contestuali e naturali.', 'Handle support, sales, and information with contextual and natural bots.') }}</p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
          <div v-for="(item, idx) in chatbotCards" :key="item.title" class="rounded-2xl p-6 border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.03] anim-el" :class="'d' + (idx + 1)">
            <component :is="item.icon" class="h-5 w-5 text-blue-500 mb-3" />
            <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">{{ item.title }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{{ item.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <section id="voice-agents" class="relative py-24 bg-gray-950 overflow-hidden" data-animate="voice" :class="{ 'is-visible': visible.voice }">
      <div class="absolute inset-0 pointer-events-none">
        <div class="particle" style="left:10%; animation-duration:15s; animation-delay:0s"></div>
        <div class="particle" style="left:32%; animation-duration:18s; animation-delay:2s"></div>
        <div class="particle" style="left:58%; animation-duration:13s; animation-delay:1s"></div>
        <div class="particle" style="left:84%; animation-duration:17s; animation-delay:4s"></div>
      </div>
      <div class="container mx-auto px-4 relative z-10">
        <div class="max-w-3xl mx-auto text-center mb-14">
          <p class="text-[13px] uppercase tracking-[0.2em] text-blue-400/70 font-medium mb-4">{{ tr('Agenti Vocali', 'Voice Agents') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">{{ tr('Esperienze vocali naturali per chiamate in entrata e uscita', 'Natural voice experiences for inbound and outbound calls') }}</h2>
          <p class="text-lg text-white/70">{{ tr('Riduci tempi di attesa e gestisci volumi elevati con agenti vocali AI multilingua.', 'Reduce waiting times and handle high volumes with multilingual AI voice agents.') }}</p>
        </div>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          <div class="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 anim-el d1">
            <h3 class="text-white font-bold text-lg mb-4">{{ tr('Capacità principali', 'Core capabilities') }}</h3>
            <ul class="space-y-2.5">
              <li v-for="f in voiceFeatures" :key="f" class="flex items-start gap-2">
                <Check class="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <span class="text-sm text-white/80">{{ f }}</span>
              </li>
            </ul>
          </div>
          <div class="grid grid-cols-1 gap-4 anim-el d2">
            <div class="rounded-xl border border-white/[0.08] bg-white/[0.03] p-4">
              <p class="text-[11px] text-white/50 mb-2">{{ tr('Live Voice Stream', 'Live Voice Stream') }}</p>
              <div class="h-12 flex items-end gap-1.5">
                <div v-for="i in 22" :key="i" class="wave-bar w-1.5 rounded-full bg-blue-400/70" :style="{ animationDelay: `${i * 0.06}s` }"></div>
              </div>
            </div>
            <div v-for="card in voiceCards" :key="card.title" class="rounded-xl border border-white/[0.08] bg-white/[0.03] p-4">
              <h4 class="text-white font-semibold mb-1">{{ card.title }}</h4>
              <p class="text-sm text-white/70">{{ card.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="process-automation" class="py-24 bg-white dark:bg-dark transition-colors duration-300" data-animate="automation" :class="{ 'is-visible': visible.automation }">
      <div class="container mx-auto px-4">
        <div class="max-w-3xl mx-auto text-center mb-14">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ tr('Automazione dei Processi', 'Process Automation') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">{{ tr('Ottimizza le operazioni aziendali con workflow intelligenti', 'Optimize operations with intelligent workflows') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400">{{ tr('Dalla gestione documentale alla pianificazione, automatizziamo processi end-to-end con controllo completo.', 'From document handling to scheduling, we automate end-to-end processes with full control.') }}</p>
        </div>

        <div class="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div class="lg:col-span-2 rounded-2xl border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-6 anim-el d1">
            <h3 class="text-gray-900 dark:text-white font-bold mb-4">{{ tr('Roadmap di implementazione', 'Implementation roadmap') }}</h3>
            <div class="space-y-3">
              <div v-for="(phase, idx) in opsPhases" :key="phase.title" class="rounded-lg border p-3 transition-colors duration-300"
                :class="idx <= activeOpsPhase ? 'border-blue-300 dark:border-blue-500/30 bg-blue-50/70 dark:bg-blue-500/10' : 'border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.02]'">
                <div class="flex items-center justify-between">
                  <p class="text-sm font-semibold" :class="idx <= activeOpsPhase ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'">{{ phase.title }}</p>
                  <span class="text-[11px]" :class="idx <= activeOpsPhase ? 'text-blue-500' : 'text-gray-400'">{{ phase.days }}</span>
                </div>
                <p class="text-xs mt-1.5 text-gray-500 dark:text-gray-400">{{ phase.desc }}</p>
              </div>
            </div>
          </div>

          <div class="lg:col-span-3 space-y-5 anim-el d2">
            <div class="rounded-2xl border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.02] p-5">
              <div class="flex flex-wrap gap-2 mb-4">
                <button v-for="(flow, idx) in opsFlows" :key="flow.name" @click="activeOpsFlow = idx"
                  class="px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-300"
                  :class="activeOpsFlow === idx ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-white/[0.05] text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/[0.09]'">
                  {{ flow.name }}
                </button>
              </div>
              <div class="rounded-xl border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-4">
                <p class="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">Workflow in esecuzione</p>
                <div class="flex flex-wrap items-center gap-2">
                  <template v-for="(step, i) in opsFlows[activeOpsFlow].steps" :key="step">
                    <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.03] px-3 py-1.5 text-xs"
                      :class="i < activeOpsStep ? 'text-blue-600 dark:text-blue-300 border-blue-300 dark:border-blue-500/30' : 'text-gray-600 dark:text-gray-300'">
                      {{ step }}
                    </div>
                    <span v-if="i < opsFlows[activeOpsFlow].steps.length - 1" class="text-gray-300 dark:text-gray-600">→</span>
                  </template>
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-300 mt-4">{{ opsFlows[activeOpsFlow].description }}</p>
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div class="rounded-xl border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-4 text-center">
                <p class="text-[11px] text-gray-400 dark:text-gray-500 mb-1">{{ tr('Tempo risparmiato', 'Time saved') }}</p>
                <p class="text-2xl font-bold text-blue-600 dark:text-blue-300 tabular-nums">{{ cOpsTimeSaved }}h</p>
              </div>
              <div class="rounded-xl border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-4 text-center">
                <p class="text-[11px] text-gray-400 dark:text-gray-500 mb-1">{{ tr('Errori ridotti', 'Reduced errors') }}</p>
                <p class="text-2xl font-bold text-green-600 dark:text-green-300 tabular-nums">-{{ cOpsErrors }}%</p>
              </div>
              <div class="rounded-xl border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-4 text-center">
                <p class="text-[11px] text-gray-400 dark:text-gray-500 mb-1">{{ tr('Processi attivi', 'Active processes') }}</p>
                <p class="text-2xl font-bold text-violet-600 dark:text-violet-300 tabular-nums">{{ cOpsProcesses }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="marketing-automation" class="py-24 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="marketing" :class="{ 'is-visible': visible.marketing }">
      <div class="container mx-auto px-4">
        <div class="max-w-3xl mx-auto text-center mb-14">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ tr('Marketing Automatizzato', 'Automated Marketing') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">{{ tr('Campagne AI che si ottimizzano in continuo', 'AI campaigns that optimize continuously') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400">{{ tr('Contenuti, segmenti e budget vengono ottimizzati dinamicamente in base ai risultati.', 'Content, segments, and budget are dynamically optimized based on results.') }}</p>
        </div>

        <div class="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div class="lg:col-span-3 rounded-2xl border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.02] p-5 anim-el d1">
            <div class="flex flex-wrap gap-2 mb-4">
              <button v-for="(goal, idx) in marketingGoals" :key="goal.title" @click="activeMarketingGoal = idx"
                class="px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-300"
                :class="activeMarketingGoal === idx ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-white/[0.05] text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/[0.09]'">
                {{ goal.title }}
              </button>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-3">
                <p class="text-[10px] text-gray-400 dark:text-gray-500 mb-1">CTR medio</p>
                <p class="text-xl font-bold text-gray-900 dark:text-white">{{ marketingGoals[activeMarketingGoal].ctr }}</p>
              </div>
              <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-3">
                <p class="text-[10px] text-gray-400 dark:text-gray-500 mb-1">CPA</p>
                <p class="text-xl font-bold text-gray-900 dark:text-white">{{ marketingGoals[activeMarketingGoal].cpa }}</p>
              </div>
              <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-3">
                <p class="text-[10px] text-gray-400 dark:text-gray-500 mb-1">ROAS</p>
                <p class="text-xl font-bold text-blue-600 dark:text-blue-300">{{ marketingGoals[activeMarketingGoal].roas }}</p>
              </div>
            </div>
            <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-4">
              <p class="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">Andamento 30 giorni</p>
              <div class="flex items-end gap-1.5 h-20">
                <div v-for="(h, i) in marketingGoals[activeMarketingGoal].trend" :key="i" class="flex-1 rounded-t bg-blue-500/70 marketing-bar" :style="{ '--h': `${h}%` }"></div>
              </div>
            </div>
          </div>

          <div class="lg:col-span-2 rounded-2xl border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.03] p-6 anim-el d2">
            <ul class="space-y-2.5 mb-5">
              <li v-for="f in marketingFeatures" :key="f" class="flex items-start gap-2">
                <Check class="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <span class="text-sm text-gray-600 dark:text-gray-300">{{ f }}</span>
              </li>
            </ul>
            <div class="space-y-2">
              <div v-for="card in marketingCards" :key="card.title" class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-3">
                <p class="text-sm font-semibold text-gray-800 dark:text-gray-200">{{ card.title }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ card.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="lead-generation" class="py-24 bg-white dark:bg-dark transition-colors duration-300" data-animate="leadgen" :class="{ 'is-visible': visible.leadgen }">
      <div class="container mx-auto px-4">
        <div class="max-w-3xl mx-auto text-center mb-14">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ tr('Lead Generation', 'Lead Generation') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">{{ tr('Più lead qualificati, meno spreco di budget', 'More qualified leads, less wasted budget') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400">{{ tr('Un funnel automatizzato che segmenta, qualifica e nutre i lead fino alla conversione.', 'An automated funnel that segments, qualifies, and nurtures leads up to conversion.') }}</p>
        </div>

        <div class="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div class="lg:col-span-3 rounded-2xl border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-6 anim-el d1">
            <h3 class="text-gray-900 dark:text-white font-bold mb-4">{{ tr('Funnel intelligente', 'Smart funnel') }}</h3>
            <div class="space-y-3">
              <div v-for="stage in leadFunnel" :key="stage.name">
                <div class="flex items-center justify-between mb-1.5">
                  <p class="text-sm font-semibold text-gray-700 dark:text-gray-300">{{ stage.name }}</p>
                  <span class="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{{ stage.value }}</span>
                </div>
                <div class="h-2.5 rounded-full bg-gray-200 dark:bg-white/[0.08] overflow-hidden">
                  <div class="h-full rounded-full lead-bar transition-all duration-700" :style="{ '--w': stage.width, width: visible.leadgen ? stage.width : '0%' }"></div>
                </div>
              </div>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
              <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.03] p-3 text-center">
                <p class="text-[11px] text-gray-400 dark:text-gray-500">Lead/Mese</p>
                <p class="text-xl font-bold text-gray-900 dark:text-white tabular-nums">{{ cLeadsIn }}</p>
              </div>
              <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.03] p-3 text-center">
                <p class="text-[11px] text-gray-400 dark:text-gray-500">SQL</p>
                <p class="text-xl font-bold text-blue-600 dark:text-blue-300 tabular-nums">{{ cSql }}</p>
              </div>
              <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.03] p-3 text-center">
                <p class="text-[11px] text-gray-400 dark:text-gray-500">Close Rate</p>
                <p class="text-xl font-bold text-green-600 dark:text-green-300 tabular-nums">{{ cCloseRate }}%</p>
              </div>
            </div>
          </div>

          <div class="lg:col-span-2 space-y-4 anim-el d2">
            <div class="rounded-2xl border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.03] p-5">
              <ul class="space-y-2.5">
                <li v-for="f in leadFeatures" :key="f" class="flex items-start gap-2">
                  <Check class="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span class="text-sm text-gray-600 dark:text-gray-300">{{ f }}</span>
                </li>
              </ul>
            </div>
            <div class="grid grid-cols-1 gap-3">
              <div v-for="card in leadCards" :key="card.title" class="rounded-xl border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-4">
                <component :is="card.icon" class="h-5 w-5 text-blue-500 mb-2" />
                <h4 class="font-semibold text-gray-900 dark:text-white mb-1">{{ card.title }}</h4>
                <p class="text-sm text-gray-500 dark:text-gray-400">{{ card.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="relative py-28 md:py-36 overflow-hidden" data-animate="cta" :class="{ 'is-visible': visible.cta }">
      <div class="absolute inset-0 bg-gray-950">
        <img src="/ciobai_hero_background.webp" alt="" class="absolute inset-0 w-full h-full object-cover opacity-60" />
      </div>
      <div class="container mx-auto px-4 text-center relative z-10">
        <div class="max-w-4xl mx-auto">
          <h2 class="text-4xl md:text-5xl font-extrabold text-white mb-4 anim-el d1">{{ tr('Costruiamo la tua architettura di automazione AI', 'We build your AI automation architecture') }}</h2>
          <p class="text-xl mb-8 text-white/85 max-w-3xl mx-auto anim-el d2">{{ tr('Analizziamo i tuoi processi e creiamo un sistema su misura per vendite, supporto e operations.', 'We analyze your processes and build a custom system for sales, support, and operations.') }}</p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center items-center anim-el d3">
            <router-link to="/contact" class="inline-flex items-center h-14 px-8 bg-white text-gray-900 font-bold text-lg rounded-xl">
              <Sparkles class="mr-2 h-5 w-5" />
              {{ tr('Richiedi una consulenza', 'Request a consultation') }}
            </router-link>
            <router-link to="/#automations" class="inline-flex items-center h-14 px-8 border border-white/20 text-white rounded-xl font-semibold text-lg">
              {{ tr('Vedi i servizi in home', 'See services on home') }}
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import {
  Bot, Check, Lock, Sparkles, PhoneCall, Mic, Mail,
  Target, MessageCircle, UserPlus, Sprout, GitBranch, HeadphonesIcon,
  ShoppingCart, BookOpen, Presentation
} from 'lucide-vue-next'
import { useTheme } from '../composables/useTheme'
import { useI18n } from 'vue-i18n'

const { isDark } = useTheme()
const { locale } = useI18n()
const visible = reactive({})
const tr = (it, en) => ((locale.value || '').startsWith('en') ? en : it)
let observer = null

const cTasks = ref(0)
const cHours = ref(0)
const activePipelineStep = ref(0)
const activeOpsFlow = ref(0)
const activeOpsPhase = ref(0)
const activeOpsStep = ref(0)
const activeMarketingGoal = ref(0)

const cOpsTimeSaved = ref(0)
const cOpsErrors = ref(0)
const cOpsProcesses = ref(0)
const cLeadsIn = ref(0)
const cSql = ref(0)
const cCloseRate = ref(0)
const activeScenario = ref(0)
const heroPanel = ref(null)
const mx = ref(0)
const my = ref(0)

function onHeroMove(e) {
  if (!heroPanel.value) return
  const r = heroPanel.value.getBoundingClientRect()
  mx.value = ((e.clientX - r.left) / r.width - 0.5) * 2
  my.value = ((e.clientY - r.top) / r.height - 0.5) * 2
}

function onHeroLeave() {
  mx.value = 0
  my.value = 0
}

const mockupTransform = computed(() => ({
  transform: `perspective(1200px) rotateY(${mx.value * 5}deg) rotateX(${-my.value * 5}deg)`,
  transition: mx.value === 0 && my.value === 0 ? 'transform 0.6s cubic-bezier(.22,1,.36,1)' : 'transform 0.08s ease'
}))

const pipelineSteps = [
  { label: 'Capture' },
  { label: 'Classify' },
  { label: 'Route' },
  { label: 'Resolve' }
]

const opsPhases = [
  { title: 'Discovery', days: '1-2 gg', desc: 'Mappiamo processi, colli di bottiglia e KPI.' },
  { title: 'Design', days: '3-5 gg', desc: 'Progettiamo workflow, trigger e fallback.' },
  { title: 'Integrazione', days: '5-10 gg', desc: 'Colleghiamo CRM, ERP, email e strumenti interni.' },
  { title: 'Ottimizzazione', days: 'continuo', desc: 'Misuriamo e miglioriamo ogni automazione.' }
]

const opsFlows = [
  {
    name: 'Documenti',
    description: 'Classificazione automatica documenti, estrazione dati e routing al team corretto.',
    steps: ['Upload', 'OCR+AI', 'Validation', 'CRM Update', 'Notification']
  },
  {
    name: 'Pianificazione',
    description: 'Gestione appuntamenti con conferme automatiche, reminder e ripianificazione smart.',
    steps: ['Request', 'Availability', 'Book', 'Reminder', 'Follow-up']
  },
  {
    name: 'Supporto',
    description: 'Smistamento ticket intelligente con priorità dinamica e SLA monitorato.',
    steps: ['Ticket', 'Priority AI', 'Assign', 'Resolve', 'Report']
  }
]

const marketingGoals = [
  {
    title: 'Acquisizione',
    ctr: '4.8%',
    cpa: 'CHF 42',
    roas: '4.2x',
    trend: [24, 30, 28, 36, 38, 44, 48, 54, 58, 62, 70, 78]
  },
  {
    title: 'Retargeting',
    ctr: '6.1%',
    cpa: 'CHF 28',
    roas: '5.7x',
    trend: [18, 26, 32, 34, 42, 46, 50, 56, 64, 68, 74, 82]
  },
  {
    title: 'Nurturing',
    ctr: '5.4%',
    cpa: 'CHF 31',
    roas: '4.9x',
    trend: [20, 24, 30, 36, 40, 44, 49, 53, 59, 63, 69, 76]
  }
]

const leadFunnel = [
  { name: 'Lead acquisiti', value: '1,420', width: '100%' },
  { name: 'Lead qualificati', value: '940', width: '78%' },
  { name: 'Opportunity', value: '510', width: '52%' },
  { name: 'SQL', value: '260', width: '35%' },
  { name: 'Clienti', value: '96', width: '18%' }
]

const scenarios = [
  {
    title: 'Customer Care 24/7',
    subtitle: 'Chat + ticket routing',
    trigger: 'Messaggio cliente',
    action: 'Risposta + ticket',
    latency: '< 1s',
    impact: '-62% tempi supporto',
    flow: ['Chat inbound', 'Intent AI', 'Knowledge lookup', 'Risposta', 'Escalation CRM']
  },
  {
    title: 'Voice Agent Inbound',
    subtitle: 'Centralino intelligente',
    trigger: 'Chiamata entrante',
    action: 'Smistamento smart',
    latency: '1.4s',
    impact: '+41% first-call resolution',
    flow: ['Call inbound', 'ASR', 'Intent detect', 'Action', 'Summary log']
  },
  {
    title: 'Email & Scheduling',
    subtitle: 'Inbox operativa automatica',
    trigger: 'Nuova email',
    action: 'Reply + calendario',
    latency: '3.2s',
    impact: '-55% carico operativo',
    flow: ['Email parse', 'Priority score', 'Reply draft', 'Calendar booking', 'Follow-up']
  },
  {
    title: 'Lead Qualification',
    subtitle: 'Funnel e nurturing',
    trigger: 'Nuovo lead',
    action: 'Score + nurture',
    latency: '2.6s',
    impact: '+37% lead qualificati',
    flow: ['Lead capture', 'Enrichment', 'Scoring', 'Segmentation', 'Campaign trigger']
  }
]

const chatbotCards = [
  { icon: HeadphonesIcon, title: 'Servizio Clienti', description: 'Aiuta i tuoi lead e clienti 24/7 istantaneamente.' },
  { icon: ShoppingCart, title: 'Assistente Vendite', description: 'Naviga cataloghi complessi con facilità, aumentando le conversioni.' },
  { icon: BookOpen, title: 'Informativo', description: 'Educa clienti e membri del team con accesso immediato alle informazioni.' },
  { icon: Presentation, title: 'Tutor', description: 'Fornisce guida passo-passo per operazioni complesse.' }
]

const voiceFeatures = [
  'Riconoscimento vocale avanzato',
  'Risposte naturali e fluide',
  'Gestione automatizzata delle chiamate',
  'Integrazione con sistemi esistenti',
  'Analisi delle conversazioni vocali',
  'Supporto multilingue'
]
const voiceCards = [
  { icon: PhoneCall, title: 'Chiamate in Entrata e in Uscita', description: 'Gestisci richieste in arrivo e fai chiamate in uscita come un segretario virtuale.' },
  { icon: Mic, title: 'Comandi Vocali', description: 'Abilita operazioni controllate vocalmente per interazioni a mani libere.' },
  { icon: Bot, title: 'Assistente Virtuale', description: 'Assistenti vocali potenziati da IA per interazioni complesse e naturali.' }
]

const marketingFeatures = [
  'Personalizzazione dei contenuti',
  'Segmentazione automatica',
  'Ottimizzazione delle campagne',
  'Analisi predittiva',
  'Automazione dei social media',
  'Marketing via email intelligente'
]
const marketingCards = [
  { icon: Target, title: 'Pubblicità IA', description: 'Crea e ottimizza campagne pubblicitarie su più piattaforme.' },
  { icon: MessageCircle, title: 'Automazione dei Social Media', description: 'Pianifica, pubblica e analizza automaticamente i contenuti social.' },
  { icon: Mail, title: 'Marketing via Email', description: 'Progetta, invia e ottimizza campagne email con assistenza IA.' }
]

const leadFeatures = [
  'Identificazione lead basata su IA',
  'Nurturing automatizzato',
  'Qualificazione dei lead',
  'Ottimizzazione del funnel',
  'Campagne mirate',
  'Analisi predittiva'
]
const leadCards = [
  { icon: UserPlus, title: 'Identificazione Lead', description: 'Individua opportunità ad alta probabilità di conversione.' },
  { icon: Sprout, title: 'Nurturing Automatizzato', description: 'Mantieni il contatto giusto al momento giusto lungo il funnel.' },
  { icon: GitBranch, title: 'Ottimizzazione Funnel', description: 'Ottimizza ogni passaggio con decisioni data-driven.' }
]

function countUp(target, end, duration) {
  const start = performance.now()
  function tick(now) {
    const p = Math.min((now - start) / duration, 1)
    const eased = 1 - Math.pow(1 - p, 3)
    target.value = Math.round(eased * end)
    if (p < 1) requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
}

watch(() => visible.hero, (v) => {
  if (!v) return
  countUp(cTasks, 18472, 2200)
  countUp(cHours, 1240, 2200)
  activePipelineStep.value = 0
  ;[500, 1100, 1700, 2300].forEach((delay, i) => {
    setTimeout(() => { activePipelineStep.value = i + 1 }, delay)
  })
})

watch(() => visible.automation, (v) => {
  if (!v) return
  countUp(cOpsTimeSaved, 780, 1700)
  countUp(cOpsErrors, 68, 1700)
  countUp(cOpsProcesses, 47, 1700)
  activeOpsPhase.value = 0
  ;[350, 900, 1450, 2000].forEach((delay, idx) => {
    setTimeout(() => { activeOpsPhase.value = idx }, delay)
  })
  activeOpsStep.value = 0
  ;[500, 900, 1300, 1700, 2200].forEach((delay, idx) => {
    setTimeout(() => { activeOpsStep.value = idx + 1 }, delay)
  })
})

watch(() => activeOpsFlow.value, () => {
  activeOpsStep.value = 0
  ;[350, 700, 1050, 1400, 1750].forEach((delay, idx) => {
    setTimeout(() => { activeOpsStep.value = idx + 1 }, delay)
  })
})

watch(() => visible.leadgen, (v) => {
  if (!v) return
  countUp(cLeadsIn, 1420, 1600)
  countUp(cSql, 260, 1600)
  countUp(cCloseRate, 37, 1600)
})

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) visible[entry.target.dataset.animate] = true
    })
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })
  document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el))
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

defineOptions({ name: 'AutomazioniAiPage' })
</script>

<style scoped>
.hero-gradient-text {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.tabular-nums { font-variant-numeric: tabular-nums; }

.anim-el {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1);
}
.is-visible .anim-el { opacity: 1; transform: translateY(0); }
.d1 { transition-delay: 0.05s; }
.d2 { transition-delay: 0.15s; }
.d3 { transition-delay: 0.25s; }
.d4 { transition-delay: 0.35s; }
.d5 { transition-delay: 0.45s; }
.d6 { transition-delay: 0.55s; }

.particle {
  position: absolute;
  bottom: -10px;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: rgba(96, 165, 250, 0.4);
  animation: particleRise linear infinite;
}
@keyframes particleRise {
  0% { transform: translateY(0) translateX(0); opacity: 0; }
  5% { opacity: 0.8; }
  50% { transform: translateY(-50vh) translateX(20px); opacity: 0.4; }
  100% { transform: translateY(-100vh) translateX(-10px); opacity: 0; }
}

.wave-bar {
  height: 8px;
  animation: voiceWave 1.2s ease-in-out infinite;
}
@keyframes voiceWave {
  0%, 100% { height: 8px; opacity: 0.45; }
  50% { height: 36px; opacity: 1; }
}

.marketing-bar {
  height: 0;
  transition: height 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.is-visible .marketing-bar {
  height: var(--h);
}
.marketing-bar:nth-child(1) { transition-delay: 0.05s; }
.marketing-bar:nth-child(2) { transition-delay: 0.09s; }
.marketing-bar:nth-child(3) { transition-delay: 0.13s; }
.marketing-bar:nth-child(4) { transition-delay: 0.17s; }
.marketing-bar:nth-child(5) { transition-delay: 0.21s; }
.marketing-bar:nth-child(6) { transition-delay: 0.25s; }
.marketing-bar:nth-child(7) { transition-delay: 0.29s; }
.marketing-bar:nth-child(8) { transition-delay: 0.33s; }
.marketing-bar:nth-child(9) { transition-delay: 0.37s; }
.marketing-bar:nth-child(10) { transition-delay: 0.41s; }
.marketing-bar:nth-child(11) { transition-delay: 0.45s; }
.marketing-bar:nth-child(12) { transition-delay: 0.49s; }

.lead-bar {
  background: linear-gradient(90deg, #60a5fa 0%, #3b82f6 70%, #2563eb 100%);
}
</style>
